/**
 * Shared user-facing messages for auth state transitions.
 * Used by both web and mobile to ensure consistent messaging.
 */

// Login-intercepted states (shown when login/OAuth returns a special state)
export const AUTH_STATE_MESSAGES = {
  accountPaused: {
    title: 'Account Paused',
    description: 'Your account is currently paused. Would you like to resume?',
    actionLabel: 'Resume Account',
    loadingLabel: 'Resuming...',
  },
  accountPendingDeletion: {
    title: 'Account Scheduled for Deletion',
    description: (email: string, dateStr?: string) =>
      `Your account (${email}) is scheduled for permanent deletion${dateStr ? ` on ${dateStr}` : ''}. Would you like to recover it?`,
    mobileDescription: (dateStr?: string) =>
      `Your account is scheduled for permanent deletion${dateStr ? ` on ${dateStr}` : ''}. Would you like to recover it?`,
    actionLabel: 'Recover Account',
    loadingLabel: 'Recovering...',
  },
} as const;

// Success messages after resume/recovery
export const AUTH_SUCCESS_MESSAGES = {
  resumed: {
    title: 'Welcome Back',
    message: 'Your account has been resumed successfully.',
    toast: 'Welcome back! Your account has been resumed.',
  },
  recovered: {
    title: 'Account Recovered',
    message: 'Your account deletion has been cancelled. Welcome back!',
    toast: 'Account recovered! Deletion has been cancelled.',
  },
} as const;

// Stale-token logout reasons (shown when token refresh fails due to account state)
export const LOGOUT_REASON_MESSAGES = {
  paused: {
    title: 'Account Paused',
    description: 'Your account has been paused. Log in to resume your account.',
  },
  deleted: {
    title: 'Account Deleted',
    description: 'Your account is scheduled for deletion. Log in to recover it.',
  },
} as const;

// Dispute-intercepted states (shown as banners when disputeStatus is set)
export const DISPUTE_STATE_MESSAGES = {
  activeDispute: {
    title: 'Payment Dispute Active',
    description:
      'A payment dispute is being reviewed on your account. Your subscription has been temporarily revoked.',
  },
  disputeBanned: {
    title: 'Account Suspended',
    description:
      'Your account has been suspended due to repeated payment disputes. Contact support to resolve.',
  },
} as const;

// Dispute checkout block message
export const DISPUTE_CHECKOUT_BLOCKED =
  'Your account has been suspended due to repeated payment disputes. Contact support.' as const;

export const ADMIN_BILLING_UNAVAILABLE =
  'Admin accounts have no billing. Plans are shown here for review only.' as const;

// Defensive fallback messages for getUserFriendlyError (web) and mapAuthError (mobile)
// Keys are matched via string inclusion, so partial matches work (e.g. "User not found" matches "User not found")
export const AUTH_FRIENDLY_ERRORS: Record<string, string> = {
  // Code flags — intercepted by login screens, but mapped here as safety net
  account_paused: 'Your account is paused. Log in to resume.',
  account_pending_deletion: 'Your account is scheduled for deletion. Log in to recover it.',
  email_verification_required: 'Please verify your email before signing in.',

  // Technical errors that should never reach users as-is
  'User not found': 'Something went wrong. Please try again.',
  'Token refresh failed': 'Your session expired. Please log in again.',
  'Internal server error': 'Something went wrong. Please try again.',
  'Database not available': 'Something went wrong. Please try again.',
  'Database not initialized': 'Something went wrong. Please try again.',
};

// Fallback messages used when the server returns an error status but no error message in the body.
// Keyed by operation so both web and mobile use the same strings.
export const AUTH_OPERATION_FALLBACKS = {
  login: 'Unable to sign in. Please try again later.',
  register: 'Registration failed. Please try again.',
  forgotPassword: 'Failed to send reset email. Please try again.',
  resetPassword: 'Password reset failed. Please try again.',
  changePassword: 'Failed to change password. Please try again.',
  verifyEmail: 'Email verification failed. Please try again.',
  resendVerification: 'Failed to send verification email. Please try again.',
  requestEmailChange: 'Failed to request email change. Please try again.',
  cancelEmailChange: 'Failed to cancel email change. Please try again.',
  updateProfile: 'Failed to update profile. Please try again.',
  saveSocialLinks: 'Failed to save social links. Please try again.',
  deleteAccount: 'Failed to delete account. Please try again.',
  pauseAccount: 'Failed to pause account. Please try again.',
  resumeAccount: 'Failed to resume account. Please try again.',
  recoverAccount: 'Failed to recover account. Please try again.',
  linkOAuth: 'Failed to link account. Please try again.',
  unlinkOAuth: 'Failed to unlink account. Please try again.',
  addPassword: 'Failed to add password. Please try again.',
  acceptLegal: 'Failed to record acceptance. Please try again.',
  completeOnboarding: 'Failed to complete onboarding. Please try again.',
  oauthLogin: 'Sign-in failed. Please try again.',
} as const;

/**
 * Map a raw server error message to a user-friendly string.
 * Checks AUTH_FRIENDLY_ERRORS for pattern matches (via string inclusion).
 * Returns the original message if no pattern matches (most auth errors are already user-friendly).
 */
export function mapAuthError(message: string): string {
  for (const [pattern, friendlyMessage] of Object.entries(AUTH_FRIENDLY_ERRORS)) {
    if (message.includes(pattern)) {
      return friendlyMessage;
    }
  }
  return message;
}
